import { action, makeObservable, observable } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { GeniusLivePlayerState } from 'src/domains/sportsbook/webview/components/stream/genius/GeniusLivePlayerState';
import { streaming } from 'src_server/trpc/types';

export type MatchLeadViewType = 'overview' | 'watchLive';

export type MatchLeadStreamGateReasonType = 'unauthenticated' | 'geoRestricted' | 'notInPlay' | 'unavailable';

/** Client-side checks before mounting the Genius player */
export type MatchLeadWatchLiveClientGateType = Exclude<MatchLeadStreamGateReasonType, 'unavailable'> | 'ready';

const UK_ALLOWED_COUNTRIES = ['GB', 'AA'] as const;

export const isUkStreamTerritory = (userIpCountry: string | null): boolean => {
    if (userIpCountry === null) {
        return false;
    }

    return (UK_ALLOWED_COUNTRIES as ReadonlyArray<string>).includes(userIpCountry);
};

export const resolveWatchLiveClientGate = (params: {
    isAuthorized: boolean;
    userIpCountry: string | null;
    isInPlay: boolean;
}): MatchLeadWatchLiveClientGateType => {
    if (params.isAuthorized !== true) {
        return 'unauthenticated';
    }

    if (isUkStreamTerritory(params.userIpCountry) !== true) {
        return 'geoRestricted';
    }

    if (params.isInPlay !== true) {
        return 'notInPlay';
    }

    return 'ready';
};

export const mapGeniusAvailabilityToGateReason = (
    availability: streaming.GeniusAvailabilityType | string | null | undefined
): MatchLeadStreamGateReasonType => {
    if (availability === 'FORBIDDEN') {
        return 'geoRestricted';
    }

    if (availability === 'NOT_IN_PLAY') {
        return 'notInPlay';
    }

    return 'unavailable';
};

export class MatchLeadState {
    @observable public activeView: MatchLeadViewType = 'overview';
    @observable public playerState: GeniusLivePlayerState | null = null;
    @observable public playerGateReason: MatchLeadStreamGateReasonType | null = null;
    @observable private probedEventId: number | null = null;

    public constructor() {
        makeObservable(this);
    }

    @action public setActiveView = (view: MatchLeadViewType): void => {
        this.activeView = view;

        if (view === 'overview') {
            this.playerGateReason = null;
        }
    };

    @action public setPlayerGateReason = (reason: MatchLeadStreamGateReasonType): void => {
        this.playerGateReason = reason;
    };

    @action public handleStreamUnavailable = (availability: streaming.GeniusAvailabilityType | null): void => {
        this.playerGateReason = mapGeniusAvailabilityToGateReason(availability);
    };

    @action public syncPlayerState = (common: Common, eventId: number | null, shouldMountPlayer: boolean): void => {
        if (shouldMountPlayer === false || eventId === null) {
            this.playerState = null;
            this.probedEventId = null;
            return;
        }

        if (this.playerState === null || this.probedEventId !== eventId) {
            this.probedEventId = eventId;
            this.playerState = new GeniusLivePlayerState(common, eventId);
            this.playerGateReason = null;
        }
    };

    public resolveClientGate = (
        event: EventModel,
        isAuthorized: boolean,
        userIpCountry: string | null
    ): MatchLeadWatchLiveClientGateType => {
        return resolveWatchLiveClientGate({
            isAuthorized,
            userIpCountry,
            isInPlay: event.timeMatchInPlay === true,
        });
    };
}
